import React, { useCallback } from "react";
import { Stack } from "@certa/blocks/thanos";
import { css } from "emotion";
import type { ObjectFilterGroupProps, FilterItem } from "./types";
import { ObjectFilterCondition as ObjectFilterConditionComponent } from "./ObjectFilterCondition";
import { ObjectConditionType } from "./ObjectConditionType";
import {
  AppendQuery,
  AppendGroupQuery,
  RemoveIconButton
} from "./ObjectFilterButtons";
import {
  createEmptyCondition,
  createEmptyGroup,
  isFilterCondition,
  updateItemInGroup,
  removeItemFromGroup,
  addConditionToGroup,
  addGroupToGroup,
  buildGroupAriaLabel
} from "./utils";
import { useIntl } from "react-intl";

export const ObjectFilterGroup: React.FC<ObjectFilterGroupProps> = ({
  group,
  fieldOptions,
  fieldMapping,
  onChange,
  onRemove,
  isRootGroup = false,
  shouldEnableGroupQuery = true,
  disabled = false,
  objectTypeData,
  selectedObjectType,
  parentPath = [],
  groupPathIndexes = [0],
  operatorsByFieldType,
  enhancementRules,
  hideSmartColumns
}) => {
  const intl = useIntl();
  const handleTypeChange = useCallback(
    (newType: "all" | "any") => {
      onChange({
        ...group,
        type: newType
      });
    },
    [group, onChange]
  );

  const handleItemUpdate = useCallback(
    (itemIndex: number, updatedItem: FilterItem) => {
      const updatedGroup = updateItemInGroup(group, itemIndex, updatedItem);
      onChange(updatedGroup);
    },
    [group, onChange]
  );

  const handleItemRemove = useCallback(
    (itemIndex: number) => {
      const updatedGroup = removeItemFromGroup(group, itemIndex);

      // If removing this item would make the group empty
      if (updatedGroup.conditions.length === 0) {
        // For both root and nested groups: remove the entire group
        if (onRemove) {
          onRemove();
        }
        return;
      }

      onChange(updatedGroup);
    },
    [group, onChange, onRemove]
  );

  const handleAddCondition = useCallback(() => {
    if (!selectedObjectType) return;
    const newCondition = createEmptyCondition(selectedObjectType);
    const updatedGroup = addConditionToGroup(group, newCondition);
    onChange(updatedGroup);
  }, [group, onChange, selectedObjectType]);

  const handleAddGroup = useCallback(() => {
    if (!selectedObjectType) return;
    const newGroup = createEmptyGroup();
    // Add a default condition to the new group
    const groupWithCondition = addConditionToGroup(
      newGroup,
      createEmptyCondition(selectedObjectType)
    );
    const updatedGroup = addGroupToGroup(group, groupWithCondition);
    onChange(updatedGroup);
  }, [group, onChange, selectedObjectType]);

  return (
    <Stack
      gap="s2"
      className={reportCustomFilterGroupStyle}
      role={isRootGroup ? "treegrid" : "gridcell"}
      aria-label={buildGroupAriaLabel(groupPathIndexes, intl)}
    >
      <Stack
        direction="vertical"
        gap="s00"
        gutter="s2"
        className={shouldEnableGroupQuery ? "groupFields" : "simpleFields"}
      >
        {/* Render items from the mixed conditions array */}
        {group.conditions.length > 0 && (
          <Stack gap="s2" direction="vertical" role="rowgroup">
            {group.conditions.map((item, index) => (
              <Stack
                gap="s2"
                key={index}
                role="row"
                aria-label={`${isFilterCondition(item) ? "Condition" : "Group"} ${index + 1}`}
              >
                {/* Show AND/OR dropdown logic */}
                {shouldEnableGroupQuery && (
                  <ObjectConditionType
                    customFilterValue={group}
                    conditionIndex={index}
                    onChange={handleTypeChange}
                  />
                )}

                {isFilterCondition(item) ? (
                  // Render individual condition
                  <ObjectFilterConditionComponent
                    condition={item}
                    conditionIndex={index}
                    onUpdate={(updatedCondition, conditionIndex) =>
                      handleItemUpdate(conditionIndex, updatedCondition)
                    }
                    onRemove={conditionIndex =>
                      handleItemRemove(conditionIndex)
                    }
                    objectTypeData={objectTypeData}
                    availableFields={fieldOptions}
                    canRemove={true}
                    disabled={disabled}
                    operatorsByFieldType={operatorsByFieldType}
                    enhancementRules={enhancementRules}
                    hideSmartColumns={hideSmartColumns}
                    selectedObjectType={selectedObjectType}
                  />
                ) : (
                  // Render nested group
                  <ObjectFilterGroup
                    group={item}
                    fieldOptions={fieldOptions}
                    fieldMapping={fieldMapping}
                    onChange={updatedGroup =>
                      handleItemUpdate(index, updatedGroup)
                    }
                    onRemove={() => handleItemRemove(index)}
                    isRootGroup={false}
                    shouldEnableGroupQuery={shouldEnableGroupQuery}
                    disabled={disabled}
                    objectTypeData={objectTypeData}
                    selectedObjectType={selectedObjectType}
                    parentPath={[...parentPath, "conditions", index]}
                    groupPathIndexes={[...groupPathIndexes, index]}
                    operatorsByFieldType={operatorsByFieldType}
                    enhancementRules={enhancementRules}
                    hideSmartColumns={hideSmartColumns}
                  />
                )}
              </Stack>
            ))}
          </Stack>
        )}

        {/* Append buttons */}
        {!disabled && (
          <Stack gap="s2" className="append-buttons" role="gridcell">
            <AppendQuery onClick={handleAddCondition} />
            {shouldEnableGroupQuery && (
              <AppendGroupQuery onClick={handleAddGroup} />
            )}
          </Stack>
        )}
      </Stack>

      {/* Remove button for non-root groups */}
      {!isRootGroup && onRemove && (
        <RemoveIconButton onClick={onRemove} ariaLabel="Remove group" />
      )}
    </Stack>
  );
};

const reportCustomFilterGroupStyle = css({
  "& .groupFields": {
    border: "1px solid var(--neutral-50)",
    borderRadius: "var(--s1)"
  },
  /* Simple Query Needs to grow to full parent width */
  "& .simpleFields": {
    flexGrow: 1
  },
  "& .conditionTypeText": {
    minWidth: "90px",
    paddingLeft: "var(--s4)",
    paddingTop: "var(--s4)"
  },
  '& [class$="container"]': {
    minWidth: "90px"
  },
  "& .append-buttons": {
    marginLeft: "var(--s2)",
    marginBottom: "var(--s2)",
    marginTop: "var(--s2)"
  }
});
